<script setup lang="ts">
import { useAppI18n } from '#imports'
import type { ComponentPublicInstance } from 'vue'

import { createSeoBreadcrumbList, createSeoWebSite, resolveSeoUrl } from '~/composables/useSeoJsonLd'
import { useSeoHead } from '~/composables/useSeoHead'
import { ensureHomeStoreLocalePrefix, getHomeStoreAppRankingSlug } from '~/utils/homeStore'
import {
  getSoftwareDownloadRanking,
  getSoftwareMonthlyActiveRanking,
  type SoftwareRankingItem,
} from '~/api/software-ranking'
import { getRankCategories, getRankList, type RankCategoryItem, type RankListResponse } from '~/server/ranking'

type RankingTabId = string
type BusinessTabId = number
type AppRankingSubTab = RankCategoryItem['subTabs'][number] & { data: SoftwareRankingItem[] }

const RANK_LIST_PAGE = 1
const RANK_LIST_PAGE_SIZE = 50
const RANK_LIST_SKELETON_ROWS = 8
const RANK_LIST_SKELETON_RENDER_DELAY = 500
const CRYPTO_REGULATION_TAB_ID = '82200010'
const POPULARITY_TAB_ID = '82100019'
const ASSET_RESERVE_TAB_ID = '82200092'
const APP_DOWNLOAD_TAB_ID = '92200010'
const APP_DOWNLOAD_IOS_SLUG = 'appdownload-ios'
const APP_DOWNLOAD_ANDROID_SLUG = 'appdownload-android'
const APP_DOWNLOAD_IOS_SUBTAB = 'iosdownload'
const APP_DOWNLOAD_ANDROID_SUBTAB = 'androiddownload'
const EMPTY_RANK_LIST: RankListResponse = { traderRank: [], desc: '' }
const SOFTWARE_TRADER_LINK_ICON =
  'https://resources1.interface003.com/web30/2026/09/11/385af5e8-7ede-420b-878b-68ec798ee5bf.png'

/** 使用当前地区排行，当前地区没有数据时回退到全球排行。 */
const resolveSoftwareRankingList = (data: { global: SoftwareRankingItem[]; current: SoftwareRankingItem[] }) =>
  data.current.length > 0 ? data.current : data.global

/** 下载指数统一使用英文千分位分隔，兼容接口可能已带逗号的字符串。 */
const formatDownloadIndex = (value: unknown) => {
  const numberValue = typeof value === 'number' ? value : Number(String(value ?? '').replaceAll(',', ''))

  return Number.isFinite(numberValue) ? numberValue.toLocaleString('en-US') : '--'
}

/** APP 信息列没有返回内容时显示占位符，同时保留数值 0。 */
const displayAppValue = (value: unknown) =>
  value === null || value === undefined || value === '' ? '--' : String(value)

const route = useRoute()
const matomo = useMatomo()

const { locale, localePath, localeMeta, t } = useAppI18n()
const appDownloadDescription = computed(() => t('89344'))

/** 从 catch-all slug 中提取实际业务段，只消费第一个路由片段。 */
const routeSlug = computed(() => {
  const slug = route.params.slug

  if (Array.isArray(slug)) {
    return slug[0] ?? ''
  }

  return slug ?? ''
})

/** 按最后一个横杠拆分 slug，前半段作为一级分类 englishName。 */
const routeRankingTabId = computed(() => {
  if ([APP_DOWNLOAD_IOS_SLUG, APP_DOWNLOAD_ANDROID_SLUG].includes(routeSlug.value)) {
    return 'appdownload'
  }

  const businessSeparatorIndex = routeSlug.value.lastIndexOf('-')

  if (businessSeparatorIndex < 0) {
    return routeSlug.value
  }

  return routeSlug.value.slice(0, businessSeparatorIndex)
})

/** 按最后一个横杠拆分 slug，后半段作为业务类型 englishName。 */
const routeBusinessTabLabel = computed(() => {
  if (routeSlug.value === APP_DOWNLOAD_IOS_SLUG) {
    return APP_DOWNLOAD_IOS_SUBTAB
  }

  if (routeSlug.value === APP_DOWNLOAD_ANDROID_SLUG) {
    return APP_DOWNLOAD_ANDROID_SUBTAB
  }

  const businessSeparatorIndex = routeSlug.value.lastIndexOf('-')

  if (businessSeparatorIndex < 0) {
    return undefined
  }

  return routeSlug.value.slice(businessSeparatorIndex + 1)
})

const rankingBreadData = computed(() => {
  return [
    {
      label: t('82520'),
      route: '/',
      color: '#7D818A',
    },
    {
      label: `${activeRankingItem.value?.name || ''}${activeBusinessTab.value ? `-${activeBusinessTab.value}` : ''}`,
      route: '',
      color: '#292A2E',
    },
  ]
})

/** 兼容历史数字业务类型 slug。 */
const routeBusinessTabId = computed(() => {
  const parsedBusinessType = Number.parseInt(routeBusinessTabLabel.value ?? '', 10)

  return Number.isFinite(parsedBusinessType) ? (parsedBusinessType as BusinessTabId) : undefined
})

/** 从 subid 查询参数读取二级 Tab 的 businessType。 */
const routeSubBusinessTypeId = computed(() => {
  const queryValue = Array.isArray(route.query.subid) ? route.query.subid[0] : route.query.subid

  if (queryValue === undefined || queryValue === null || queryValue === '') {
    return undefined
  }

  const parsedBusinessType = Number(queryValue)

  return Number.isInteger(parsedBusinessType) ? (parsedBusinessType as BusinessTabId) : undefined
})

// ─── 1. 获取排行榜分类数据 ─────────────────────────────────────────────

const { data: rankCategoriesData } = await useAsyncData(
  'best-exchanges-ranking-categories-app',
  async () => {
    const [rankingResult, iosDownloadResult, androidDownloadResult, iosActiveResult, androidActiveResult] =
      await Promise.allSettled([
        getRankCategories(),
        getSoftwareDownloadRanking(1),
        getSoftwareDownloadRanking(2),
        getSoftwareMonthlyActiveRanking(1),
        getSoftwareMonthlyActiveRanking(2),
      ])
    const rankingData = rankingResult.status === 'fulfilled' ? rankingResult.value : { data: { items: [] } }
    const iosDownload =
      iosDownloadResult.status === 'fulfilled' ? resolveSoftwareRankingList(iosDownloadResult.value) : []
    const androidDownload =
      androidDownloadResult.status === 'fulfilled' ? resolveSoftwareRankingList(androidDownloadResult.value) : []
    const iosActive = iosActiveResult.status === 'fulfilled' ? resolveSoftwareRankingList(iosActiveResult.value) : []
    const androidActive =
      androidActiveResult.status === 'fulfilled' ? resolveSoftwareRankingList(androidActiveResult.value) : []
    const appSubTabs: AppRankingSubTab[] = [
      ...(iosDownload.length > 0
        ? [{ businessType: 1, name: `iOS${t('89330')}`, englishName: 'iosdownload', data: iosDownload }]
        : []),
      ...(androidDownload.length > 0
        ? [{ businessType: 2, name: `Android${t('89330')}`, englishName: 'androiddownload', data: androidDownload }]
        : []),
      ...(iosActive.length > 0
        ? [{ businessType: 3, name: `iOS${t('89331')}`, englishName: 'iosactive', data: iosActive }]
        : []),
      ...(androidActive.length > 0
        ? [{ businessType: 4, name: `Android${t('89331')}`, englishName: 'androidactive', data: androidActive }]
        : []),
    ]

    const rankingItems = rankingData.data?.items ?? []
    const hasAppRanking = rankingItems.some((item) => item.code === APP_DOWNLOAD_TAB_ID)

    return {
      ...rankingData.data,
      items: hasAppRanking
        ? rankingItems.map((item) =>
            item.code === APP_DOWNLOAD_TAB_ID
              ? {
                  ...item,
                  subTabs: appSubTabs,
                }
              : item,
          )
        : rankingItems,
    }
  },
  {
    // server: false,
  },
)

/** 接口返回的排行榜分类列表。 */
const rankCategoryItems = computed<RankCategoryItem[]>(() => rankCategoriesData.value?.items ?? [])

/** 一级排行榜分类 tabs。 */
const rankingTabs = computed(() =>
  rankCategoryItems.value.map((item) => ({
    id: item.code,
    label: item.name,
    flag: item.flag,
    urlLabel: item.englishName || item.name,
  })),
)

// ─── 2. 手动同步一级分类激活态（替代 watch） ─────────────────────────────

/** 当前激活的一级排行榜分类。 */
const activeTab = ref<RankingTabId>('')

/** 根据路由和接口数据直接赋值一级分类。 */
const resolveActiveTab = (): RankingTabId => {
  const tabVal =
    rankingTabs.value.find((tab) => tab.urlLabel === routeRankingTabId.value) ||
    rankingTabs.value.find((tab) => tab.label === routeRankingTabId.value)
  return tabVal?.id || rankingTabs.value[0]?.id || ''
}

activeTab.value = resolveActiveTab()

/** 当前一级分类的完整接口数据。 */
const activeRankingItem = computed(() => rankCategoryItems.value.find((item) => item.code === activeTab.value))

/** 当前一级分类独有的业务类型 tabs。 */
const businessTabs = computed(() =>
  (activeRankingItem.value?.subTabs ?? []).map((subTab) => ({
    id: subTab.businessType,
    label: subTab.name,
    urlLabel: subTab.englishName || subTab.name,
  })),
)

/** APP Tab 直接读取当前二级 Tab 附带的全球排行数据，不走交易所排行榜接口。 */
const activeAppRankingData = computed<SoftwareRankingItem[]>(() => {
  if (!isAppDownloadTab.value) return []

  const activeSubTab = activeRankingItem.value?.subTabs.find((subTab) => subTab.businessType === activeBusinessId.value)

  return (activeSubTab as AppRankingSubTab | undefined)?.data ?? []
})

/** 按软件详情页约定拼接市场、应用、交易商与展示平台参数。 */
const getSoftwareDetailUrl = (app: SoftwareRankingItem) => {
  const code = [app.marketId, app.appBundleId, app.traderCode, app.showPlatform]
    .map((value) => encodeURIComponent(String(value ?? '')))
    .join('-')

  const detailPath = ensureHomeStoreLocalePrefix(`/crypto-apps/${code}?fromType=3`, locale)

  return new URL(detailPath, 'https://store.wikibit.com').toString()
}

/** 在新窗口打开软件详情，跳转规则与 WikiBit Store 页面保持一致。 */
const openSoftwareDetail = (app: SoftwareRankingItem) => {
  return navigateTo(getSoftwareDetailUrl(app), { external: true, open: { target: '_blank' } })
}

const commonBusinessTabs = computed(() =>
  businessTabs.value.map((tab) => ({
    label: tab.label,
    value: tab.id,
  })),
)

// ─── 3. 手动同步业务类型激活态（替代 watch） ────────────────────────────

/** 当前激活的业务类型（名称）。 */
const activeBusinessTab = ref<string | undefined>(undefined)

/** 当前激活的业务类型的 ID，供后续调用接口使用。 */
const activeBusinessId = ref<BusinessTabId | undefined>(undefined)

/** 根据路由和业务 tabs 直接赋值业务类型。 */
const resolveActiveBusinessTab = () => {
  const targetTab =
    businessTabs.value.find((tab) => tab.id === routeSubBusinessTypeId.value) ||
    businessTabs.value.find((tab) => tab.urlLabel === routeBusinessTabLabel.value) ||
    businessTabs.value.find((tab) => tab.label === routeBusinessTabLabel.value) ||
    businessTabs.value.find((tab) => tab.id === routeBusinessTabId.value) ||
    businessTabs.value[0]
  return targetTab
}

const initialBusinessTab = resolveActiveBusinessTab()
activeBusinessTab.value = initialBusinessTab?.label || ''
activeBusinessId.value = initialBusinessTab?.id

/** 排行榜页面统一使用当前语言的 TDK 描述，供页面 head 与 JSON-LD 复用。 */
const rankingDescription = computed(() => t('84363', [activeRankingItem.value?.name || '']))

useSeoHead({
  title: () => t('84397', [activeRankingItem.value?.name || '']),
  description: rankingDescription,
  keywords: () => t('84397', [activeRankingItem.value?.name || '']).replace(/\s*\|\s*WikiBit\s*$/i, ''),
  ogType: 'website',
  includeSocial: true,
  includeCurrentHreflang: true,
})

const rankingCanonicalUrl = useSeoCanonicalUrl()

/** 加密监管列只属于监管排行榜，其他排行榜需要隐藏这一列。 */
const shouldShowCryptoRegulation = computed(() => activeTab.value === CRYPTO_REGULATION_TAB_ID)

/** APP 下载排行使用专属表头，避免沿用交易所排行字段。 */
const isAppDownloadTab = computed(() => activeTab.value === APP_DOWNLOAD_TAB_ID)

/** 资产排行固定展示交易所列名，其他排行显示当前选中的业务类型。 */
const activeBusinessColumnLabel = computed(() => {
  if (activeTab.value === ASSET_RESERVE_TAB_ID) {
    return t('80337')
  }

  return activeBusinessTab.value || t('80337')
})

/** 根据当前排行榜类型决定最后一个指标列展示评分、人气值或资产储备金。 */
const activeMetricColumn = computed(() => {
  if (activeTab.value === POPULARITY_TAB_ID) {
    return {
      label: t('84423'),
    }
  }

  if (activeTab.value === ASSET_RESERVE_TAB_ID) {
    return {
      label: t('84422'),
    }
  }

  return {
    label: t('82384'),
  }
})

// ─── 4. 直接请求排行榜列表数据（替代 computed key + watch） ──────────────

const { data: rankListData } = await useAsyncData(
  'ranking',
  async () => {
    if (isAppDownloadTab.value) {
      return EMPTY_RANK_LIST
    }

    const params = {
      keyCode: activeTab.value,
      businessType: activeBusinessId.value,
      page: RANK_LIST_PAGE,
      pageSize: RANK_LIST_PAGE_SIZE,
    }
    const rankListResponse = await getRankList(params as any)
    return rankListResponse.data
  },
  {
    // server: false,
  },
)

/** 清洗排行榜名称，优先使用当前语言简称，避免把接口备注拼接到 JSON-LD。 */
const resolveRankingItemName = (item: {
  localShortName?: string
  showName?: string
  shortName?: string
  fullName?: string
}) => {
  const candidates = [item.localShortName, item.showName, item.shortName, item.fullName]

  for (const candidate of candidates) {
    const normalized = candidate
      ?.replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^(?:中文|英文)\s*/u, '')
      .replace(/\s*(?:中文|英文)$/u, '')
      .trim()
    if (normalized) {
      return normalized
    }
  }

  return ''
}

const rankingJsonLd = computed(() => {
  const pageUrl = rankingCanonicalUrl.value
  const siteUrl = new URL(pageUrl).origin
  const rankingName = activeRankingItem.value?.name || ''
  const pageTitle = t('84397', [rankingName])
  const itemListId = `${pageUrl}#itemlist`
  const rankingRows = (rankListData.value?.traderRank || []) as Array<{
    alias?: string
    showName?: string
    localShortName?: string
    shortName?: string
    fullName?: string
  }>
  const rankingItems = rankingRows
    .slice(0, 5)
    .filter((item) => item?.alias && resolveRankingItemName(item))
    .map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: resolveRankingItemName(item),
      url: resolveSeoUrl(localePath({ name: 'exchange-detail', params: { slug: item.alias } }), pageUrl),
    }))
  const breadcrumb = createSeoBreadcrumbList(
    [
      { label: t('82520'), route: localePath('/') },
      { label: `${rankingName}${activeBusinessTab.value ? `-${activeBusinessTab.value}` : ''}` },
    ],
    pageUrl,
  )

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: pageTitle,
        description: rankingDescription.value,
        inLanguage: localeMeta.language,
        isPartOf: createSeoWebSite(siteUrl, true),
        mainEntity: {
          '@id': itemListId,
        },
      },
      {
        '@type': 'ItemList',
        '@id': itemListId,
        name: pageTitle,
        description: rankingDescription.value,
        inLanguage: localeMeta.language,
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: rankingItems.length,
        itemListElement: rankingItems,
      },
      breadcrumb,
    ],
  }
})
useSeoJsonLd(rankingJsonLd)

/** 记录客户端列表请求数量，避免连续切换标签时提前结束骨架屏。 */
const rankListRequestCount = ref(0)
const isRankListLoading = computed(() => rankListRequestCount.value > 0)

/** 等待新列表完成 DOM 更新后，再结束骨架屏以避免出现空白帧。 */
const waitForRankListRender = async () => {
  await nextTick()
  await new Promise<void>((resolve) => window.setTimeout(resolve, RANK_LIST_SKELETON_RENDER_DELAY))
}

// ─── 5. 指示器相关 ─────────────────────────────────────────────────────

const tabRefs = new Map<RankingTabId, HTMLElement>()
const rankingTabsNavRef = ref<HTMLElement | null>(null)
const indicatorLeft = ref(0)
const isIndicatorReady = ref(false)
const isIndicatorTransitionEnabled = ref(false)
let indicatorTransitionFrameId: number | undefined

const clinetRequest = async () => {
  if (isAppDownloadTab.value) {
    rankListData.value = EMPTY_RANK_LIST
    return
  }

  rankListRequestCount.value++

  try {
    const params = {
      keyCode: activeTab.value,
      businessType: activeBusinessId.value,
      page: RANK_LIST_PAGE,
      pageSize: RANK_LIST_PAGE_SIZE,
    }
    const rankListResponse = await getRankList(params as any)
    rankListData.value = rankListResponse.data
  } finally {
    await waitForRankListRender()
    rankListRequestCount.value--
  }
}
/** 记录一级分类按钮元素，用于计算指示器位置。 */
const setTabRef = (id: RankingTabId, element: Element | ComponentPublicInstance | null) => {
  if (element instanceof HTMLElement) {
    tabRefs.set(id, element)
    return
  }

  tabRefs.delete(id)
}

/** 路由切换重渲染后，将焦点还原到当前一级排行榜，保持后续 Tab 顺序连续。 */
const restoreRankingTabFocus = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>('.ranking-tabs button[aria-current="page"]')?.focus({
        preventScroll: true,
      })
    })
  })
}

/** 路由切换重渲染后，将焦点还原到当前业务 Tab，保留原有选中状态，仅展示焦点阴影。 */
const restoreBusinessTabFocus = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>('.business-tabs .common-tab[aria-selected="true"]')?.focus()
    })
  })
}

/** 首次完成定位后一帧再开启动画，避免初始位置从 0 滑入。 */
const enableIndicatorTransition = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (isIndicatorTransitionEnabled.value || indicatorTransitionFrameId !== undefined) {
    return
  }

  indicatorTransitionFrameId = window.requestAnimationFrame(() => {
    isIndicatorTransitionEnabled.value = true
    indicatorTransitionFrameId = undefined
  })
}

/** 关闭指示器动画，用于首次定位、语言切换、窗口变化等纯布局重排场景。 */
const disableIndicatorTransition = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (indicatorTransitionFrameId !== undefined) {
    window.cancelAnimationFrame(indicatorTransitionFrameId)
    indicatorTransitionFrameId = undefined
  }

  isIndicatorTransitionEnabled.value = false
}

/** 静默定位完成后一帧再恢复动画，避免从旧位置或 0 位置滑入。 */
const restoreIndicatorTransitionAfterPaint = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (indicatorTransitionFrameId !== undefined) {
    window.cancelAnimationFrame(indicatorTransitionFrameId)
  }

  indicatorTransitionFrameId = window.requestAnimationFrame(() => {
    indicatorTransitionFrameId = window.requestAnimationFrame(() => {
      isIndicatorTransitionEnabled.value = true
      indicatorTransitionFrameId = undefined
    })
  })
}

/** 更新顶部一级分类激活态指示器的位置。 */
const updateIndicator = (shouldAnimate = true) => {
  const activeElement = tabRefs.get(activeTab.value)

  if (!activeElement) {
    return
  }

  if (!shouldAnimate) {
    disableIndicatorTransition()
  }

  if (shouldAnimate) {
    enableIndicatorTransition()
  }

  indicatorLeft.value = activeElement.offsetLeft + (activeElement.offsetWidth - 28) / 2
  isIndicatorReady.value = true

  if (!shouldAnimate) {
    restoreIndicatorTransitionAfterPaint()
  }
}

/** 窗口尺寸变化只修正位置，不播放指示器位移动画。 */
const updateIndicatorWithoutTransition = () => updateIndicator(false)

// ─── 6. 路由 slug 工具函数 ──────────────────────────────────────────────

/** 根据一级分类和业务类型的 englishName 生成当前页面 slug。 */
const buildRankingSlug = (rankingLabel: string, businessLabel?: string) => {
  const normalizedRankingLabel = rankingLabel.trim()
  const normalizedBusinessLabel = businessLabel?.trim() ?? ''

  if (!normalizedRankingLabel) {
    return ''
  }

  if (!normalizedBusinessLabel) {
    return normalizedRankingLabel
  }

  return `${normalizedRankingLabel}-${normalizedBusinessLabel}`
}

/** 使用 Nuxt 客户端导航同步地址栏 slug，避免整页刷新。 */
const updateRouteSlug = async (rankingLabel: string, businessLabel?: string) => {
  const targetSlug = buildRankingSlug(rankingLabel, businessLabel)

  if (!targetSlug || targetSlug === routeSlug.value) {
    return
  }

  await navigateTo(
    localePath({
      name: 'best-exchanges-slug',
      params: {
        slug: [targetSlug],
      },
    }),
  )
}

// ─── 7. 用户交互：切换 Tab ──────────────────────────────────────────────

/** 为 APP 一级分类选择当前平台对应的默认子榜。 */
const resolveDefaultBusinessTab = () => {
  const platformSubTab =
    getHomeStoreAppRankingSlug() === APP_DOWNLOAD_IOS_SLUG ? APP_DOWNLOAD_IOS_SUBTAB : APP_DOWNLOAD_ANDROID_SUBTAB

  return businessTabs.value.find((tab) => tab.urlLabel === platformSubTab) || businessTabs.value[0]
}

/** 切换一级排行榜分类，并同步重置业务类型，手动刷新列表。 */
const scrollActiveTabIntoView = (id: RankingTabId, behavior: ScrollBehavior = 'smooth') => {
  const el = tabRefs.get(id)
  if (el) {
    el.scrollIntoView({ behavior, block: 'nearest', inline: 'nearest' })
  }
}

/** 判断一级 Tab 是否被横向滚动区域遮挡。 */
const isRankingTabClipped = (id: RankingTabId) => {
  const activeElement = tabRefs.get(id)
  const navElement = rankingTabsNavRef.value

  if (!activeElement || !navElement) {
    return false
  }

  return (
    activeElement.offsetLeft < navElement.scrollLeft ||
    activeElement.offsetLeft + activeElement.offsetWidth > navElement.scrollLeft + navElement.clientWidth
  )
}

const selectTab = async (id: RankingTabId) => {
  if (isRankListLoading.value) {
    return
  }

  const targetRankingTab = rankingTabs.value.find((tab) => tab.id === id)
  if (!targetRankingTab) {
    return
  }

  matomo.trackEvent('broker_list_tab', {
    af_type: targetRankingTab.label,
  })

  activeTab.value = id
  const defaultBusinessTab = id === APP_DOWNLOAD_TAB_ID ? resolveDefaultBusinessTab() : businessTabs.value[0]
  activeBusinessTab.value = defaultBusinessTab?.label
  activeBusinessId.value = defaultBusinessTab?.id

  nextTick(() => {
    // 被遮挡时让角标先静默贴合 Tab，再随容器一起滚动，避免两段动画叠加闪动。
    updateIndicator(!isRankingTabClipped(id))
    scrollActiveTabIntoView(id)
  })
  // 路由参数变化会重建当前页面，先保留实例以展示完整骨架屏。
  await clinetRequest()
  if (id === APP_DOWNLOAD_TAB_ID) {
    await updateRouteSlug(getHomeStoreAppRankingSlug())
  } else {
    await updateRouteSlug(targetRankingTab.urlLabel, defaultBusinessTab?.urlLabel)
  }
  restoreRankingTabFocus()
}

/** 切换当前一级分类下的业务类型，手动刷新列表。 */
const selectBusinessTab = async (name: string, id: BusinessTabId) => {
  if (isRankListLoading.value) {
    return
  }

  activeBusinessTab.value = name
  activeBusinessId.value = id

  const targetRankingTab = rankingTabs.value.find((tab) => tab.id === activeTab.value)
  const targetBusinessTab = businessTabs.value.find((tab) => tab.id === id)
  // 路由参数变化会重建当前页面，先保留实例以展示完整骨架屏。
  await clinetRequest()

  await updateRouteSlug(targetRankingTab?.urlLabel || routeRankingTabId.value, targetBusinessTab?.urlLabel || name)
  restoreBusinessTabFocus()
}

const onBusinessTabChange = (tab: any) => {
  const targetBusinessTab = businessTabs.value.find((tb) => tb.id === tab.value)
  if (targetBusinessTab) {
    selectBusinessTab(targetBusinessTab.label, targetBusinessTab.id as BusinessTabId)
  }
}

/** 返回前三名的特殊徽章样式。 */
const getRankBadgeClass = (rank: string) => {
  const badgeClassMap: Record<string, string> = {
    '1': 'rounded-[10px 0] bg-[#ffad33] text-white',
    '2': 'rounded-[10px 0] bg-[#c5c7d5] text-white',
    '3': 'rounded-[10px 0] bg-[#e9a28d] text-white',
  }

  return badgeClassMap[rank] ?? ''
}

/** 计算列表项展示名次，广告行只展示 AD 标识，不占用交易所排名。 */
const getNonAdRank = (rows: Array<{ isAdv?: boolean }>, index: number) => {
  return rows.slice(0, index + 1).filter((row) => !row.isAdv).length
}

const getRankIcon = (rank: number): string => {
  let icons: Record<number, string> = {}
  if (activeTab.value !== '82300010') {
    icons = {
      1: 'https://resources1.interface003.com/web30/2026/05/21/f3eefdcb-a40c-4f72-bf3c-5af3f89ef1b9.png',
      2: 'https://resources1.interface003.com/web30/2026/05/21/ba4a7977-36d5-400f-a6f7-812f2ac4fe72.png',
      3: 'https://resources1.interface003.com/web30/2026/05/21/e5e9854a-744a-43ea-8893-c9e61bbe2262.png',
    }
  } else {
    icons = {
      1: 'https://resources1.interface003.com/web30/2026/07/01/f0ab609c-ce68-45dc-af9b-f179522a5042.png',
      2: 'https://resources1.interface003.com/web30/2026/07/01/a7e206d6-c25a-4e2f-82e3-3c30678a96e3.png',
      3: 'https://resources1.interface003.com/web30/2026/07/01/78b038c3-0307-4483-8a7d-18407e456722.png',
    }
  }

  return icons[rank] || ''
}

const overflowMap = ref<Record<string, boolean>>({})

const handleMouseEnter = (e: MouseEvent, key: string) => {
  const el = e.target as HTMLElement
  if (el) {
    overflowMap.value[key] = el.scrollWidth > el.clientWidth
  }
}

// ─── 8. 生命周期 ────────────────────────────────────────────────────────

watch(
  () => rankingTabs.value.map((tab) => `${tab.id}:${tab.label}`).join('|'),
  () => {
    nextTick(() => updateIndicator(false))
  },
)

watch(
  () => route.fullPath,
  () => {
    nextTick(() => updateIndicator(false))
  },
)

const onNavScroll = () => updateIndicator(false)

onMounted(() => {
  nextTick(() => {
    // 路由切换会重建滚动容器，先同步恢复位置，避免从初始位置播放一段错误的滚动动画。
    scrollActiveTabIntoView(activeTab.value, 'auto')
    updateIndicator(false)
  })
  window.addEventListener('resize', updateIndicatorWithoutTransition)
  rankingTabsNavRef.value?.addEventListener('scroll', onNavScroll, { passive: true })
})

onUnmounted(() => {
  if (indicatorTransitionFrameId !== undefined) {
    window.cancelAnimationFrame(indicatorTransitionFrameId)
  }

  window.removeEventListener('resize', updateIndicatorWithoutTransition)
  rankingTabsNavRef.value?.removeEventListener('scroll', onNavScroll)
})
</script>

<template>
  <div class="ranking-page-shell mx-auto w-[1200px] py-[70px]">
    <CommonBreadcrumb :items="rankingBreadData" />
    <section
      class="ranking-hero relative h-[170px] overflow-hidden rounded-[16px]"
      :style="{ backgroundColor: activeTab === '82300010' ? '#E7E8EC' : '#eaf3ff' }"
    >
      <div
        class="absolute top-1/2 right-[300px] left-[58px] flex -translate-y-1/2 flex-col gap-[12px] rtl:right-[58px] rtl:left-[300px]"
      >
        <h1 class="text-[34px] leading-normal font-semibold text-[#292a2e]">
          {{ activeRankingItem?.name }}{{ activeBusinessTab ? `-${activeBusinessTab}` : '' }}
        </h1>
        <p class="line-clamp-2 text-[14px] leading-[22px] font-normal text-[#7d818a]">
          {{ isAppDownloadTab ? appDownloadDescription : rankListData?.desc }}
        </p>
      </div>

      <CommonImage
        v-if="activeTab !== '82300010'"
        src="https://resources1.interface003.com/web30/2026/06/27/090ebe2a-8eb4-48be-952f-77318a32b0f4.png"
        width="200"
        height="200"
        class="!absolute top-[30px] right-[76px] object-cover rtl:right-auto rtl:left-[76px]"
      />
      <CommonImage
        v-else
        src="https://resources1.interface003.com/web30/2026/07/01/70587494-bc56-40a9-bcb8-1f91644bdd9d.png"
        width="200"
        height="200"
        class="!absolute top-[30px] right-[76px] object-cover rtl:right-auto rtl:left-[76px]"
      />
    </section>

    <nav
      ref="rankingTabsNavRef"
      class="ranking-tabs relative mt-[24px] flex h-[60px] items-center gap-[24px] overflow-x-auto border-b border-[rgba(5,21,36,0.06)] pt-[16px]"
      aria-label="排行榜分类"
    >
      <button
        v-for="tab in rankingTabs"
        :key="tab.id"
        :ref="(element: Element | ComponentPublicInstance | null) => setTabRef(tab.id, element)"
        type="button"
        class="flex h-full cursor-pointer items-center gap-[4px] border-0 bg-transparent pb-[16px] text-center text-[20px] leading-[28px] whitespace-nowrap transition-colors duration-200"
        :class="activeTab === tab.id ? 'font-semibold text-[#292a2e]' : 'font-normal text-[#8c8f97]'"
        :aria-current="activeTab === tab.id ? 'page' : undefined"
        :tabindex="activeTab === tab.id ? 0 : -1"
        :disabled="isRankListLoading"
        @click="selectTab(tab.id)"
      >
        <CommonImage
          v-if="tab.id === '82500010' && tab.flag"
          :src="tab.flag"
          width="20"
          height="20"
          class="size-[20px] shrink-0 object-cover"
        />

        <span>{{ tab.label }}</span>
      </button>

      <span
        v-if="isIndicatorReady"
        class="pointer-events-none absolute bottom-0 left-0 h-[4px] w-[28px] rounded-[999px] bg-[#292a2e]"
        :class="{
          'transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]': isIndicatorTransitionEnabled,
        }"
        :style="{ transform: `translateX(${indicatorLeft}px)` }"
        aria-hidden="true"
      />
    </nav>

    <CommonTabs
      v-if="commonBusinessTabs.length > 0"
      v-model="activeBusinessId"
      :tabs="commonBusinessTabs"
      :disabled="isRankListLoading"
      :mask-width="48"
      aria-label="业务类型"
      class="business-tabs"
      style="
        --common-tabs-width: 100%;
        --common-tabs-max-width: 100%;
        --common-tab-font-weight: 500;
        --common-tab-padding: 4px 12px;
      "
      @change="onBusinessTabChange"
    />

    <section class="ranking-table overflow-hidden rounded-[8px]" aria-label="交易所排行榜">
      <div
        class="ranking-table-grid items-center border-b border-[#112D73] py-[12px] text-[13px] leading-[20px] text-[#7d818a]"
        :class="{
          'ranking-table-grid--without-regulation': !shouldShowCryptoRegulation,
          'ranking-table-grid--app-download': isAppDownloadTab,
        }"
      >
        <div class="text-center">#</div>
        <div v-if="isAppDownloadTab" class="px-[12px]">APP</div>
        <div class="px-[12px]">{{ isAppDownloadTab ? t('89346') : activeBusinessColumnLabel }}</div>
        <div v-if="shouldShowCryptoRegulation" class="px-[12px]">{{ t('83577') }}</div>
        <div class="px-[12px]">{{ isAppDownloadTab ? t('89347') : t('84341') }}</div>
        <div class="px-[12px]">{{ isAppDownloadTab ? t('89348') : activeMetricColumn.label }}</div>
        <div class="text-center">{{ t('84343') }}</div>
      </div>

      <div
        class="ranking-table-body relative"
        :class="{ 'h-[640px] overflow-hidden': isRankListLoading, 'min-h-[640px]': isAppDownloadTab }"
      >
        <div
          v-show="isRankListLoading"
          class="ranking-table-skeleton absolute inset-0 z-[1] bg-white"
          role="status"
          aria-live="polite"
          aria-label="Loading exchange rankings"
        >
          <div
            v-for="index in RANK_LIST_SKELETON_ROWS"
            :key="`ranking-skeleton-${index}`"
            class="ranking-table-grid h-[80px] items-center border-b-[0.5px] border-[rgba(11,18,40,0.14)]"
            :class="{
              'ranking-table-grid--without-regulation': !shouldShowCryptoRegulation,
            }"
            aria-hidden="true"
          >
            <div class="flex h-full items-center justify-center">
              <span class="ranking-skeleton-block ranking-skeleton-rank"></span>
            </div>
            <div class="flex w-[375px] shrink-0 items-center gap-[12px] px-[12px]">
              <span class="ranking-skeleton-block ranking-skeleton-logo"></span>
              <span class="flex flex-col gap-[6px]">
                <span class="ranking-skeleton-block ranking-skeleton-name"></span>
                <span class="ranking-skeleton-block ranking-skeleton-label"></span>
              </span>
            </div>
            <div v-if="shouldShowCryptoRegulation" class="flex items-center px-[12px]">
              <span class="ranking-skeleton-block ranking-skeleton-tag"></span>
            </div>
            <div class="flex items-center px-[12px]">
              <span class="ranking-skeleton-block ranking-skeleton-text"></span>
            </div>
            <div class="flex items-center px-[12px]">
              <span class="ranking-skeleton-block ranking-skeleton-score"></span>
            </div>
            <div class="flex h-full items-center justify-center px-[12px]">
              <span class="ranking-skeleton-block ranking-skeleton-action"></span>
            </div>
          </div>
        </div>

        <NuxtLink
          v-if="isAppDownloadTab && !isRankListLoading"
          v-for="(app, index) in activeAppRankingData"
          :key="app.softwareId || `${app.appBundleId}-${index}`"
          :to="getSoftwareDetailUrl(app)"
          target="_blank"
          external
          class="ranking-table-grid ranking-table-grid--app-download group h-[80px] cursor-pointer items-center border-b-[0.5px] border-[rgba(11,18,40,0.14)] transition-colors duration-200 hover:bg-[#f7f8fa]"
        >
          <div class="flex h-full items-center justify-center">
            <span
              class="flex min-h-[18px] min-w-[18px] items-center justify-center text-[14px] leading-[22px] text-[#7d818a]"
            >
              <CommonImage v-if="index + 1 <= 3" :src="getRankIcon(index + 1)" alt="" class="h-[18px] w-[18px]" />
              <span v-else>{{ index + 1 }}</span>
            </span>
          </div>
          <div class="flex min-w-0 items-center gap-[12px] px-[12px]">
            <CommonImage
              :src="app.logo"
              class="box-border size-[44px] shrink-0 rounded-[4px] border border-solid border-[rgba(5,21,36,0.06)] object-cover"
            />
            <div class="min-w-0">
              <div class="flex h-[24px] min-w-0 items-center">
                <el-tooltip
                  v-if="app.name"
                  :content="app.name"
                  placement="top"
                  popper-class="ranking-business-tooltip"
                  :show-after="0"
                  :offset="6"
                  :disabled="!app.name || overflowMap[`app-name-${app.softwareId || `${app.appBundleId}-${index}`}`] === false"
                >
                  <span
                    class="inline-block max-w-full min-w-0 truncate text-[16px] leading-[24px] font-medium text-[#292a2e]"
                    @mouseenter="handleMouseEnter($event, `app-name-${app.softwareId || `${app.appBundleId}-${index}`}`)"
                  >
                    {{ app.name }}
                  </span>
                </el-tooltip>
                <span
                  v-else
                  class="inline-block max-w-full min-w-0 truncate text-[16px] leading-[24px] font-medium text-[#292a2e]"
                >
                  --
                </span>
              </div>
              <div class="flex min-w-0 items-center gap-[4px]">
                <CommonImage :src="SOFTWARE_TRADER_LINK_ICON" alt="" class="size-[16px] shrink-0" />
                <CommonImage
                  v-if="app.traderIcon"
                  :src="app.traderIcon"
                  alt=""
                  class="size-[16px] shrink-0 rounded-[2px]"
                />
                <span class="min-w-0 truncate text-[14px] leading-[22px] text-[#0d3f63]">{{
                  app.traderName || '--'
                }}</span>
              </div>
            </div>
          </div>
          <div class="font-wiki-Medium px-[12px] text-[18px] leading-[22px]">
            {{ formatDownloadIndex(app.downloadNum ?? app.activeValue) }}
          </div>
          <div class="font-wiki-Medium px-[12px] text-[18px] leading-[22px]">{{ displayAppValue(app.sizes) }}</div>
          <div class="font-wiki-Medium px-[12px] text-[18px] leading-[22px]">
            {{ displayAppValue(app.supportRegionCount) }}
          </div>
          <div class="flex h-full items-center justify-center px-[12px]">
            <button
              type="button"
              class="h-[32px] min-w-[86px] cursor-pointer rounded-[100px] border-0 bg-[#112f70] px-[10px] text-[12px] leading-[18px] font-medium text-white transition-[background-color,transform] duration-200 hover:bg-[#173f92] active:scale-[0.97]"
              @click.stop="openSoftwareDetail(app)"
            >
              {{ t('84342') }}
            </button>
          </div>
        </NuxtLink>

        <NuxtLink
          v-if="!isAppDownloadTab"
          v-show="!isRankListLoading"
          v-for="(row, index) in rankListData?.traderRank"
          :key="row?.alias"
          :to="localePath({ name: 'exchange-detail', params: { slug: row.alias } })"
          target="_blank"
          class="ranking-table-grid group h-[80px] cursor-pointer items-center border-b-[0.5px] border-[rgba(11,18,40,0.14)] transition-colors duration-200 hover:bg-[#f7f8fa]"
          :class="{
            'ranking-table-grid--without-regulation': !shouldShowCryptoRegulation,
            'ultra-row': row.ultimateUltraIco,
          }"
        >
          <div class="flex h-full items-center justify-center">
            <span
              v-if="row.isAdv"
              class="flex size-[20px] items-center justify-center rounded-[2px] bg-[#f0f1f2] text-[11px] leading-[18px] text-[#505258]"
            >
              AD
            </span>
            <span
              v-else
              class="flex min-h-[18px] min-w-[18px] items-center justify-center text-[14px] leading-[22px] text-[#7d818a]"
            >
              <CommonImage
                v-if="getNonAdRank(rankListData?.traderRank ?? [], index) <= 3"
                :src="getRankIcon(getNonAdRank(rankListData?.traderRank ?? [], index))"
                alt=""
                class="h-[18px] w-[18px]"
              />
              <span v-else>{{ getNonAdRank(rankListData?.traderRank ?? [], index) }}</span>
            </span>
          </div>

          <div class="flex w-[375px] shrink-0 items-center gap-[12px] px-[12px]">
            <CommonImage :src="row.icoSquare" class="h-[44px] w-[44px] shrink-0 rounded-[4px] object-cover" />
            <div class="min-w-0">
              <div class="flex h-[24px] min-w-0 items-center gap-[8px]">
                <el-tooltip
                  v-if="row.showName"
                  :content="row.showName"
                  placement="top"
                  popper-class="ranking-business-tooltip"
                  :show-after="0"
                  :offset="6"
                  :disabled="!row.showName || overflowMap[`name-${row.alias}`] === false"
                >
                  <span
                    class="inline-block max-w-full min-w-0 truncate text-[16px] leading-[24px] font-medium text-[#292a2e]"
                    @mouseenter="handleMouseEnter($event, `name-${row.alias}`)"
                  >
                    {{ row.showName }}
                  </span>
                </el-tooltip>
                <CommonImage
                  v-if="row.ultimateUltraIco"
                  :src="row.ultimateUltraIco"
                  class="ultra-icon"
                  alt=""
                  loading="eager"
                />
              </div>
              <div
                class="flex h-[18px] flex-wrap items-center gap-[8px] overflow-hidden text-[12px] leading-[18px]"
                v-if="row.labels && row.labels.length > 0"
              >
                <el-tooltip
                  v-if="row.labels[0]?.content"
                  :content="row.labels[0]?.content"
                  placement="top"
                  popper-class="ranking-business-tooltip"
                  :show-after="0"
                  :offset="6"
                  :disabled="!row.labels[0]?.content || overflowMap[`label-${row.alias}`] === false"
                >
                  <span
                    class="max-w-full truncate"
                    :style="{ color: row.labels[0]?.fontColor }"
                    @mouseenter="handleMouseEnter($event, `label-${row.alias}`)"
                  >
                    {{ row.labels[0]?.content }}
                  </span>
                </el-tooltip>
                <span
                  class="h-[14px] w-px bg-[rgba(11,18,40,0.14)]"
                  v-if="row.labels[0]?.content && row.labels[1]?.content"
                ></span>
                <span :style="{ color: row.labels[1]?.fontColor }">{{ row.labels[1]?.content }}</span>
              </div>
            </div>
          </div>

          <div v-if="shouldShowCryptoRegulation" class="px-[12px]" dir="ltr">
            <el-tooltip
              v-if="row.cryptoRegulation"
              :content="row.cryptoRegulation"
              placement="top"
              popper-class="ranking-business-tooltip"
              :show-after="0"
              :offset="6"
              :disabled="!row.cryptoRegulation || overflowMap[`regulation-${row.alias}`] === false"
            >
              <span
                class="inline-flex max-w-full items-center rounded-[4px] bg-[rgba(31,132,90,0.06)] px-[8px] py-[2px] text-[12px] leading-[18px] font-medium text-[#1f845a]"
              >
                <span class="min-w-0 truncate" @mouseenter="handleMouseEnter($event, `regulation-${row.alias}`)">
                  {{ row.cryptoRegulation }}
                </span>
              </span>
            </el-tooltip>
          </div>

          <div class="flex items-center px-[12px]">
            <el-tooltip
              v-if="row.registerCountry"
              :content="row.registerCountry"
              placement="top"
              popper-class="ranking-business-tooltip"
              :show-after="0"
              :offset="6"
              :disabled="!row.registerCountry || overflowMap[`country-${row.alias}`] === false"
            >
              <span class="inline-flex max-w-full items-center text-[14px] leading-[22px] font-medium text-[#292a2e]">
                <span class="min-w-0 truncate" @mouseenter="handleMouseEnter($event, `country-${row.alias}`)">
                  {{ row.registerCountry }}
                </span>
              </span>
            </el-tooltip>
            <span v-else class="text-[14px] leading-[22px] font-medium text-[#292a2e]">--</span>
          </div>

          <div class="flex items-center gap-[8px] px-[12px]">
            <!-- <CommonImage
              v-if="activeTab !== POPULARITY_TAB_ID && activeTab !== ASSET_RESERVE_TAB_ID"
              src="https://resources1.interface003.com/web30/2026/06/27/c39ab257-18e0-423e-8579-d81791e03605.png"
              class="h-[20px] w-[20px] text-[#292a2e]"
            />
            <CommonImage
              v-if="activeTab === POPULARITY_TAB_ID"
              src="https://resources1.interface003.com/web30/2026/06/30/9d1d5cc9-1ea5-480d-92a1-d1dc999bb424.png"
              alt=""
              class="h-[20px] w-[20px]"
            />
            <CommonImage
              v-if="activeTab === ASSET_RESERVE_TAB_ID"
              src="https://resources1.interface003.com/web30/2026/06/30/29183a79-bf19-4f0c-ad55-8ceece9d42d0.png"
              alt=""
              class="h-[20px] w-[20px]"
            /> -->
            <CommonImage v-if="row.scoreIcon" :src="row.scoreIcon" class="h-[20px] w-[20px]" />
            <span class="font-wiki-Medium text-[18px] leading-[26px] font-medium text-[#292a2e]"
              >{{ row.score || '--' }}{{ row?.viewCountUnit }}</span
            >
          </div>

          <div class="flex h-full items-center justify-center px-[12px]">
            <button
              type="button"
              class="h-[32px] min-w-[86px] cursor-pointer rounded-[100px] border-0 bg-[#112f70] px-[10px] text-[12px] leading-[18px] font-medium text-white transition-[background-color,transform] duration-200 hover:bg-[#173f92] active:scale-[0.97]"
            >
              {{ t('84342') }}
            </button>
          </div>
        </NuxtLink>
      </div>
    </section>
  </div>
</template>
<style scoped>
.ranking-page-shell {
  overflow-x: clip;
}

.ranking-tabs {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}

.ranking-tabs::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}

.ranking-table-grid {
  display: grid;
  grid-template-columns:
    40px
    375px
    minmax(0, 1fr)
    minmax(0, 1fr)
    130px
    130px;
}

.ranking-table-grid--without-regulation {
  grid-template-columns:
    40px
    375px
    minmax(0, 1fr)
    130px
    130px;
}

.ranking-table-grid--app-download {
  grid-template-columns:
    40px
    375px
    minmax(0, 1fr)
    minmax(0, 1fr)
    130px
    130px;
}

.ranking-table-grid > * {
  min-width: 0;
}

.ultra-row {
  background: linear-gradient(180deg, #ffffff 0%, #f8f5ff 100%);
}

.ultra-icon {
  width: 25px;
  height: 15px;
  flex: 0 0 25px;
  vertical-align: middle;
}

.ranking-table-skeleton {
  .ranking-skeleton-block {
    display: block;
    border-radius: 6px;
    background: linear-gradient(90deg, #f0f1f2 25%, #f8f8f8 50%, #f0f1f2 75%);
    background-size: 200% 100%;
    animation: ranking-skeleton-shimmer 1.2s ease-in-out infinite;
  }

  .ranking-skeleton-rank {
    width: 18px;
    height: 18px;
  }

  .ranking-skeleton-logo {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: 4px;
  }

  .ranking-skeleton-name {
    width: 112px;
    height: 18px;
  }

  .ranking-skeleton-label {
    width: 72px;
    height: 14px;
  }

  .ranking-skeleton-text {
    width: 100%;
    max-width: 112px;
    height: 18px;
  }

  .ranking-skeleton-tag {
    width: 76px;
    height: 24px;
  }

  .ranking-skeleton-score {
    width: 72px;
    height: 20px;
  }

  .ranking-skeleton-action {
    width: 86px;
    height: 32px;
    border-radius: 100px;
  }
}

@keyframes ranking-skeleton-shimmer {
  to {
    background-position: -200% 0;
  }
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .ranking-page-shell {
    width: calc(100% - 48px);
  }
}

@media screen and (max-width: 799px) {
  .ranking-page-shell {
    width: calc(800px - 48px);
  }
}
</style>

<style lang="less">
.ranking-business-tooltip.el-popper {
  max-width: 560px;
  padding: 7px 12px !important;
  border: none !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  box-shadow:
    0 0 1px 0 rgba(0, 0, 0, 0.2),
    0 8px 20px 0 rgba(0, 0, 0, 0.12) !important;
  color: #292a2e !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 22px !important;
  text-align: center;
  white-space: normal !important;
  word-break: break-all !important;

  .el-popper__arrow::before {
    border: none !important;
    background: #ffffff !important;
  }
}
</style>
